import React, { useState, useMemo } from 'react';
import { Flame } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField, SelectField, GenderField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import {
  calculateBMR, calculateTDEE, getCalorieTargets, weeklyFatLossKg, ACTIVITY_FACTORS
} from '../../lib/calculatorUtils';

const activityOptions = Object.entries(ACTIVITY_FACTORS).map(([k, v]) => ({
  value: k, label: `${v.label}: ${v.desc}`
}));

const goalOptions = [
  { value: 'mild', label: 'Mild Fat Loss (−300 kcal/day)' },
  { value: 'moderate', label: 'Moderate Fat Loss (−500 kcal/day)' },
  { value: 'aggressive', label: 'Aggressive Fat Loss (−700 kcal/day)' }
];

const CalorieDeficitCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('female');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [activity, setActivity] = useState('moderate');
  const [goal, setGoal] = useState('moderate');

  const result = useMemo(() => {
    const a = parseInt(age, 10);
    const h = parseFloat(heightCm);
    const w = parseFloat(weightKg);
    if (!a || !h || !w || a < 10 || h < 50 || w < 20) return null;
    const bmr = calculateBMR({ gender, weightKg: w, heightCm: h, age: a });
    const tdee = calculateTDEE(bmr, activity);
    const targets = getCalorieTargets(tdee);
    const selected = targets[goal];
    return {
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      selectedCalories: selected,
      deficit: Math.round(tdee - selected),
      weeklyFatLoss: weeklyFatLossKg(tdee - selected),
      targets
    };
  }, [age, gender, heightCm, weightKg, activity, goal]);

  const inputs = (
    <div className="space-y-5" data-testid="deficit-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Your Profile</h2>
      <p className="text-slate-400 text-sm mb-5">We use the Mifflin-St Jeor equation for accurate BMR.</p>
      <div className="grid grid-cols-2 gap-3">
        <NumberField id="def-age" label="Age" value={age} onChange={setAge} placeholder="e.g. 28" suffix="yrs" min={10} max={120} testId="deficit-age-input" required />
        <GenderField value={gender} onChange={setGender} testIdPrefix="deficit-gender" />
      </div>
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <SelectField id="def-activity" label="Activity Level" value={activity} onChange={setActivity} options={activityOptions} testId="deficit-activity-select" required />
      <SelectField id="def-goal" label="Fat Loss Goal" value={goal} onChange={setGoal} options={goalOptions} testId="deficit-goal-select" required />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Daily Deficit Target" value={result.selectedCalories.toLocaleString()} unit="kcal/day" accent="from-orange-500 to-amber-500" testId="deficit-result-calories" />
      <div className="mt-8 space-y-1">
        <StatRow label="BMR (resting)" value={result.bmr.toLocaleString()} unit="kcal" testId="deficit-bmr" />
        <StatRow label="TDEE (maintenance)" value={result.tdee.toLocaleString()} unit="kcal" testId="deficit-tdee" />
        <StatRow label="Daily deficit" value={result.deficit.toLocaleString()} unit="kcal" color="text-orange-400" testId="deficit-amount" />
        <StatRow label="Estimated weekly fat loss" value={result.weeklyFatLoss} unit="kg/week" color="text-emerald-400" testId="deficit-weekly-loss" />
      </div>

      <div className="mt-6 bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
        <p className="text-slate-300 text-sm font-semibold mb-3">All deficit options</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            { key: 'mild', label: 'Mild', sub: '−300' },
            { key: 'moderate', label: 'Moderate', sub: '−500' },
            { key: 'aggressive', label: 'Aggressive', sub: '−700' }
          ].map((opt) => (
            <div key={opt.key} className={`rounded-lg p-3 border ${goal === opt.key ? 'border-orange-500/40 bg-orange-500/10' : 'border-slate-700 bg-slate-900/40'}`} data-testid={`deficit-option-${opt.key}`}>
              <p className="text-slate-400 text-xs">{opt.label}</p>
              <p className="text-white font-bold text-base sm:text-lg">{result.targets[opt.key].toLocaleString()}</p>
              <p className="text-slate-500 text-[10px]">{opt.sub} kcal</p>
            </div>
          ))}
        </div>
      </div>

      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        Avoid aggressive deficits long-term. Sustainable fat loss is typically 0.5–1.0 kg per week. Discuss with a clinician if you have any underlying conditions.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your age, gender, height, weight and activity level to see your calorie deficit." />
  );

  return (
    <CalculatorLayout
      slug="calorie-deficit"
      title="Calorie Deficit Calculator"
      subtitle="Calculate your daily calorie target for safe, science-backed fat loss using BMR, TDEE and your chosen deficit level."
      metaDescription="Calculate your daily calorie deficit using the Mifflin-St Jeor formula. TCUBE FITNESS science-backed fat-loss calculator."
      icon={Flame}
      accentGradient="from-orange-500 to-amber-500"
      accentColor="text-orange-400"
      badgeBg="bg-orange-500/10"
      badgeBorder="border-orange-500/30"
      inputs={inputs}
      result={resultNode}
      formulaText={
        <>
          <p><strong className="text-slate-300">Step 1: BMR (Mifflin-St Jeor):</strong></p>
          <p>Male: BMR = (10 × kg) + (6.25 × cm) − (5 × age) + 5</p>
          <p>Female: BMR = (10 × kg) + (6.25 × cm) − (5 × age) − 161</p>
          <p className="mt-3"><strong className="text-slate-300">Step 2: TDEE:</strong> TDEE = BMR × activity factor (1.2–1.9)</p>
          <p><strong className="text-slate-300">Step 3: Deficit:</strong> Mild −300, Moderate −500, Aggressive −700 kcal/day</p>
        </>
      }
    />
  );
};

export default CalorieDeficitCalculatorPage;
